import debug from 'debug';

import { purgeTrackingArtifacts } from './purgeTrackingArtifacts';

const debugLog = debug('global:consent');

/**
 * A consent selection: integration display name → granted.
 *
 * Intentionally looser than `IntegrationData`. The funnel reuses that type both for the
 * integration *config* coming from layout data (`{ Perspective: { apiUrl } }`) and for the
 * *selection* the banner produces (`{ Perspective: true }`). The gate only cares whether a
 * value is truthy, so it accepts either shape.
 */
export type ConsentSelection = Record<string, unknown>;

/**
 * Coerce a stored/incoming selection into a plain object.
 *
 * Preferences are `JSON.parse`d out of localStorage, so at runtime they can be anything a
 * corrupted, hand-edited or half-written entry contains — `null`, a string, an array.
 * Anything that isn't a plain object is treated as "no decision recorded", which leaves the
 * gate closed so the banner asks again. Without this a stored `null` threw out of
 * `Object.keys()` during layout init and took the whole funnel down with it.
 */
const asSelection = (value: unknown): ConsentSelection =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as ConsentSelection)
        : {};

/**
 * Display name of Perspective's own (first-party) analytics integration.
 *
 * Mirrors `integrationsToDisplayName[integrations.perspective]`, duplicated here on
 * purpose: the gate must be importable from the tracking layer without importing the
 * tracking layer back (it is consulted from `tracking/helper.shouldTrack`).
 */
export const PERSPECTIVE_INTEGRATION = 'Perspective';

/**
 * CAP-4147 / § 25 TDDDG: Perspective's own funnel analytics is **not** strictly necessary
 * for the funnel to function. It sets persistent identifiers (`ajs_anonymous_id`,
 * `ajs_user_id`, `perspective__user__id`) purely for measurement, so it requires opt-in
 * consent just like any third-party integration.
 *
 * Flip to `false` only if the classification is revisited (see CAP-4042) — the rest of the
 * gate keeps working unchanged, "Reject all" would then still allow first-party analytics.
 */
export const PERSPECTIVE_REQUIRES_CONSENT = true;

/**
 * May Perspective's own first-party measurement run *before* the visitor has answered the
 * banner?
 *
 * CAP-4147 deferred it until they answered, which means a visitor who lands and leaves
 * without touching the banner produces no event, so no `Session` and no row in any analytics
 * view. Since published funnels are pinned to their publish-time build, that gap opened
 * gradually as funnels were republished, leaving every republished funnel with a step change
 * in its own history: visitors down, conversion rate up, for reasons the customer did not
 * cause. Before CAP-4147 every page load created a session, so opening the gate at load
 * restores the historical basis rather than inventing a new one.
 *
 * What this does **not** change — all of it still waits for explicit consent:
 * analytics.js, every third-party integration, `ajs_*` cookies, the localStorage retry
 * queue, and any persistent identifier. Measured against CAP-4147's three promises (no
 * cookies, no localStorage entries, no tracking requests before the answer) this breaks
 * exactly the third, and only for requests to the funnel's own origin.
 *
 * Flip to `false` to restore CAP-4147's timing exactly; nothing else needs touching.
 */
export const MEASURE_BEFORE_DECISION = true;

export type ConsentStatus = 'pending' | 'granted' | 'denied';

let status: ConsentStatus = 'pending';
let listeners: Array<() => void> = [];
let resolvedListeners: Array<() => void> = [];
let decisionListeners: Array<() => void> = [];

export const getConsentStatus = (): ConsentStatus => status;

/**
 * Has the visitor actually answered the banner, either way?
 *
 * Distinct from `isFirstPartyTrackingAllowed()`, which `MEASURE_BEFORE_DECISION` makes true
 * from page load. This is the gate on *persisting* anything: measurement may run before the
 * visitor has expressed a preference, but nothing is written to their device until they do.
 */
export const hasConsentDecision = (): boolean => status !== 'pending';

/**
 * May we load analytics.js and third-party integrations?
 *
 * Only with explicit consent. Segment's analytics.js sets `ajs_*` cookies the moment it
 * loads and every third-party integration is someone else's script, so both stay behind a
 * real opt-in.
 *
 * Fails closed: until something explicitly resolves the gate, nothing is allowed.
 */
export const isTrackingAllowed = (): boolean => status === 'granted';

/**
 * May Perspective's own first-party funnel analytics run?
 *
 * Events go straight to the funnel's own origin (`/ps-fnl-events/...`) via
 * `tracking/firstPartyDispatch`, so no third party sees them and a rejection no longer
 * suppresses our own measurement.
 *
 * With `MEASURE_BEFORE_DECISION` this is true from page load, so the visit is counted even
 * if the banner is never answered. What a pending visitor gets is exactly what a rejecting
 * one gets: a tab-scoped session id and first-party events. No cookie, no localStorage
 * entry, no analytics.js, no third party — those still require `isTrackingAllowed()`.
 */
export const isFirstPartyTrackingAllowed = (): boolean =>
    MEASURE_BEFORE_DECISION || status === 'granted' || status === 'denied';

/**
 * Run `callback` as soon as tracking is allowed.
 *
 * Runs synchronously when consent is already granted. While pending *or denied* the
 * callback stays queued, so a visitor who first rejects and later opts in via
 * "Manage Cookies" still gets a working tracking stack without a page reload.
 */
export const onConsentGranted = (callback: () => void): void => {
    if (status === 'granted') {
        callback();

        return;
    }

    listeners.push(callback);
};

/**
 * Run `callback` as soon as first-party measurement is permitted. Runs synchronously if it
 * already is — which, with `MEASURE_BEFORE_DECISION`, is immediately.
 *
 * This is the hook for anything that only needs *first-party* tracking to be permitted —
 * chiefly the tracking bootstrap. Unlike `onConsentGranted` it does not wait for an opt-in,
 * which is what lets a rejecting (or silent) visitor still be measured in our own analytics.
 */
export const onConsentResolved = (callback: () => void): void => {
    if (isFirstPartyTrackingAllowed()) {
        callback();

        return;
    }

    resolvedListeners.push(callback);
};

/**
 * Run `callback` once the visitor has answered the banner, either way. Runs synchronously if
 * they already have.
 *
 * The counterpart to `onConsentResolved`: that one fires as soon as measurement is permitted,
 * which is immediately. This one waits for an actual answer, and is the hook for anything
 * that may only touch the visitor's device once they have expressed a preference — chiefly
 * persisting the session id, so that until then a reload starts a fresh visit rather than
 * being recognised.
 */
export const onConsentDecision = (callback: () => void): void => {
    if (hasConsentDecision()) {
        callback();

        return;
    }

    decisionListeners.push(callback);
};

/** Drain a listener list, snapshotting first so a callback may register another. */
const flush = (queue: Array<() => void>): Array<() => void> => {
    queue.forEach((callback) => callback());

    return [];
};

export const grantConsent = (): void => {
    if (status === 'granted') return;

    debugLog('-> consent granted');
    status = 'granted';

    // Resolved listeners first, and the order is load-bearing: the tracking bootstrap runs
    // there, and the consent-only listeners (loading analytics.js) need the session it
    // creates to already exist. Snapshot before draining — a callback may register another.
    const queuedResolved = resolvedListeners;
    resolvedListeners = [];
    flush(queuedResolved);

    // Before the consent-only listeners: those load analytics.js, which needs the session
    // that this persists to already be on the device.
    const queuedDecision = decisionListeners;
    decisionListeners = [];
    flush(queuedDecision);

    const queued = listeners;
    listeners = [];
    flush(queued);
};

export const denyConsent = (): void => {
    const wasPending = status === 'pending';

    debugLog('-> consent denied');
    status = 'denied';
    purgeTrackingArtifacts();

    // Only on the pending -> denied transition. A second rejection (via "Manage Cookies")
    // must not re-run the bootstrap.
    if (wasPending) {
        const queuedResolved = resolvedListeners;
        resolvedListeners = [];
        flush(queuedResolved);

        // After purgeTrackingArtifacts() above, so what it persists is not immediately
        // removed again.
        const queuedDecision = decisionListeners;
        decisionListeners = [];
        flush(queuedDecision);
    }
};

/**
 * Resolve the gate from an explicit consent selection — either a banner interaction or
 * preferences restored from a previous visit.
 */
export const applyConsentSelection = (selection: ConsentSelection): void => {
    const normalized = asSelection(selection);
    const perspectiveAllowed =
        !PERSPECTIVE_REQUIRES_CONSENT || Boolean(normalized[PERSPECTIVE_INTEGRATION]);

    if (perspectiveAllowed) {
        grantConsent();
    } else {
        denyConsent();
    }
};

interface ConsentGateOptions {
    /** `data.cookieOverlay.layout` — 'none' means the funnel has no consent banner. */
    bannerLayout: string;
    /** `embedOptions.cookieBanner` — false when the embedding host owns consent. */
    bannerEnabled: boolean;
    /** `embedOptions.preview` — the editor preview never delivers events. */
    isPreview: boolean;
    /**
     * Preferences persisted by a previous visit, from `getCookiePreferences()`. Typed as
     * `unknown` on purpose: it is parsed from localStorage and cannot be trusted to be a
     * well-formed selection.
     */
    storedPreferences: unknown;
}

/**
 * Decide the gate's starting state. Must run before the tracking service is created.
 *
 * Only the last branch leaves the gate closed — that is the case CAP-4147 is about: a
 * banner is shown, the visitor has not answered it yet, so nothing may be stored or sent.
 */
export const initConsentGate = ({
    bannerLayout,
    bannerEnabled,
    isPreview,
    storedPreferences,
}: ConsentGateOptions): void => {
    resetConsentGate();

    // Preview mode is gated separately in shouldTrack(), which never sends anything.
    // Opening the gate keeps preview behaviour (session ids, embed variables) intact.
    if (isPreview) {
        debugLog('-> gate open: preview mode');
        grantConsent();

        return;
    }

    // Embedded without our banner — the host page is responsible for collecting consent.
    if (!bannerEnabled) {
        debugLog('-> gate open: cookie banner disabled by embed options');
        grantConsent();

        return;
    }

    // The funnel has no consent banner configured at all.
    if (bannerLayout === 'none') {
        debugLog('-> gate open: no banner configured');
        grantConsent();

        return;
    }

    const preferences = asSelection(storedPreferences);

    if (Object.keys(preferences).length > 0) {
        debugLog('-> resolving gate from stored preferences', preferences);
        applyConsentSelection(preferences);

        return;
    }

    debugLog('-> gate closed: awaiting banner interaction');
};

/** Test seam — resets module state between cases. */
export const resetConsentGate = (): void => {
    status = 'pending';
    listeners = [];
    resolvedListeners = [];
    decisionListeners = [];
};
